import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Module from '../components/Module';
import { logVerticalImpression } from '../utils/tracking';
import { buildApiUrl } from '../utils/api';
import { fetchJsonWithRetry } from '../utils/apiWithRetry';
import { ArrowLeftIcon } from '../components/icons';
import '../styles/design-system.css';
import './Category.css';

function Category() {
  const { categoryKey } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const loggedImpressionsRef = useRef(new Set());

  useEffect(() => {
    // Fetch modules for the selected category
    const apiUrl = buildApiUrl(`/api/modules/?category=${categoryKey}`);
    console.log('Fetching modules from:', apiUrl);
    
    fetchJsonWithRetry(apiUrl, {}, 3)
      .then(data => {
        console.log('Modules loaded:', data.length);
        setModules(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching modules after retries:', error);
        setLoading(false);
      });
  }, [categoryKey]);

  // Track vertical impressions (same logic as original App.js)
  const observerSetupRef = useRef(false);
  
  useEffect(() => {
    if (modules.length === 0 || loading) return;

    if (observerSetupRef.current) return;
    observerSetupRef.current = true;

    const moduleElements = document.querySelectorAll('.module-container');
    
    if (moduleElements.length === 0) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('Page hidden - pausing impression tracking');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const impressionObserver = new IntersectionObserver(
      (entries) => {
        if (document.hidden) {
          return;
        }
        
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const moduleElement = entry.target;
            const moduleIndex = parseInt(moduleElement.dataset.moduleIndex, 10);
            const module = modules[moduleIndex];
            
            const impressionKey = module?.vertical_id ? `${module.vertical_id}-${moduleIndex}` : null;
            
            if (!impressionKey || loggedImpressionsRef.current.has(impressionKey)) {
              return;
            }
            
            if (module && module.id && module.category_id && module.vertical_id) {
              loggedImpressionsRef.current.add(impressionKey);
              
              const visibilityRatio = entry.intersectionRatio;
              const viewportPosition = visibilityRatio >= 0.9 ? 'fully_visible' : 
                                      visibilityRatio >= 0.75 ? 'mostly_visible' : 
                                      'partially_visible';
              
              logVerticalImpression(
                module.vertical_id,
                module.category_id,
                moduleIndex,
                viewportPosition
              );
              
              impressionObserver.unobserve(moduleElement);
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '0px'
      }
    );

    moduleElements.forEach((moduleElement, index) => {
      const module = modules[index];
      const impressionKey = module?.vertical_id ? `${module.vertical_id}-${index}` : null;
      
      if (impressionKey && loggedImpressionsRef.current.has(impressionKey)) {
        return;
      }
      
      impressionObserver.observe(moduleElement);
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (impressionObserver) {
        moduleElements.forEach((module) => {
          impressionObserver.unobserve(module);
        });
        impressionObserver.disconnect();
      }
      observerSetupRef.current = false;
    };
  }, [modules, loading]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="category-page">
        <div className="category-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="category-page">
      <header className="category-header">
        <div className="category-header-content">
          <button onClick={handleBackToHome} className="back-button">
            <ArrowLeftIcon size={20} color="currentColor" />
            Back to Categories
          </button>
          <h1 className="category-site-title">The internet's new home page</h1>
          <div className="category-header-user-info">
            <span>{user?.full_name || user?.email}</span>
            <button onClick={handleLogout} className="category-header-logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="category-main-content">
        {modules.map((module, index) => (
          <div key={module.id} data-module-index={index} className="module-container">
            <Module module={module} />
          </div>
        ))}
      </main>

      <footer className="category-footer">
        <p>VKA Prototype</p>
      </footer>
    </div>
  );
}

export default Category;

